/**
 * @module shop/product-details
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
import { AddSkuItemsToNonLineInterface } from '@support/api/proxies/requests/api-2107.interface';
import { HARDGOODS_CONSTANTS } from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';

export class AddDeviceToNonLineItemsRequestModel {
  /**
   * Holds Device sku number
   */
  private skuNumber: string;
  /**
   * Holds device offer Id
   */
  private offerId: string;
  /**
   * Holds Quantity
   */
  private quantity: string;
  /**
   * Holds price of the device
   */
  private pricingOption: string;
  /**
   * Holds cart item id of the device coming from cart
   */
  private cartItemId: string;
  /**
   * Holds product subtype of the device
   */
  private productSubType: string;
  /**
   * Holds primary IMEI of the device
   */
  private primaryImei: string;
  /**
   * Holds secondary IMEI for the device
   */
  private secondaryImei: string;
  /**
   * Holds sim number of the devoce
   */
  private simNumber: string;
  /**
   * Holds IMSI of the device
   */
  private imsi: string;
  /**
   * Hods device count and increment by 1 if product type is device
   */
  public deviceCount: number;
  /**
   * Hods device count and increment by 1 if product type is accessory
   */
  public accessoryCount: number;

  constructor(
    sku?: string,
    offerId?: string,
    quantity?: string,
    pricingOption?: string,
    cartItemId?: string,
    productSubType?: string,
    imei?: string,
    sim?: string,
    imsi?: string
  ) {
    this.deviceCount = 0;
    this.accessoryCount = 0;
  }

  /**
   * Function used to set IMEI details
   * @param primaryImei
   * @param secondaryImei
   */
  public setImei(primaryImei: string, secondaryImei: string): void {
    this.primaryImei = primaryImei;
    this.secondaryImei = secondaryImei;
  }

  /**
   * Function used to set SKU details
   * @param skuNum
   * @param offerId
   * @param productType
   * @param productSubType
   * @param quantity
   * @param amount
   * @param cartItemId
   */
  public setSkuDetails(
    skuNum: string,
    offerId?: string,
    productType?: string,
    productSubType?: string,
    quantity?: string,
    amount?: string,
    cartItemId?: string
  ): void {
    this.skuNumber = skuNum;
    this.offerId = offerId;
    this.quantity = quantity;
    this.pricingOption = amount;
    this.cartItemId = cartItemId || null;
    this.productSubType = productSubType || '';
    this.updateDeviceCount(productType);
  }

  /**
   * Function used to increment device / accessory count
   * @param productType
   */
  private updateDeviceCount(productType: string): void {
    if (productType) {
      if (productType.toLocaleLowerCase() === HARDGOODS_CONSTANTS.ACCESSORY) {
        this.accessoryCount++;
      } else if (productType.toLocaleUpperCase() === HARDGOODS_CONSTANTS.DEVICE) {
        this.deviceCount++;
      }
    }
  }

  /**
   * Function used to set sim details
   * @param simNumber
   * @param imsi
   */
  public setSimDetails(simNumber: string, imsi: string): void {
    this.simNumber = simNumber;
    this.imsi = imsi;
  }

  /**
   * Function sed to get device details
   */
  public getDeviceInfo(): AddSkuItemsToNonLineInterface {
    const skuItem: AddSkuItemsToNonLineInterface = {};
    skuItem.sku = this.skuNumber;
    skuItem.offerId = this.offerId;
    skuItem.quantity = this.quantity || '1';
    skuItem.pricingOption = this.pricingOption || 'FULL';
    skuItem.cartItemId = this.cartItemId;
    skuItem.imei = this.primaryImei;
    skuItem.productSubType = this.productSubType;
    skuItem.imsi = this.imsi;
    skuItem.simNumber = this.simNumber;
    return skuItem;
  }

  /**
   * Function used to get total devices added
   */
  public getTotalDevicesAdded(): number {
    return this.deviceCount + this.accessoryCount;
  }
}
